import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  BabyIcon,
  BirdIcon,
  CatIcon,
  DicesIcon,
  EggIcon,
  EyeIcon,
  FeatherIcon,
  FishIcon,
  FlagIcon,
  FlameIcon,
  MoonIcon,
  MinusIcon,
  PawPrintIcon,
  PlusIcon,
  RotateCcwIcon,
  ShoppingBagIcon,
  WormIcon,
  XIcon,
  SparklesIcon,
} from "lucide-react";

import { Column } from "../Column";
import { Row } from "../Row";
import { Text } from "../Text";
import { StafferPetAccessorySlots } from "./StafferPetAccessorySlots";
import { StafferPetSprite } from "./StafferPetSprite";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { useStafferPet } from "../../hooks/useStafferPets";
import { useUpsertPet } from "@/hooks/updateSets/useUpsertPet";
import { stafferPets } from "../../lib/stafferPets";
import { cn } from "../../lib/utils";
import type {
  Pet,
  StafferPetPalette,
  StafferPetPaletteId,
  StafferPetSpecies,
  StafferPetTopperOffset,
} from "../../types";

const COLOR_ROLL_INTERVAL_MS = 105;
const COLOR_ROLL_DURATION_MS = 2_200;
const PET_PREVIEW_SIZE_PX = 208;

const paletteOptions = (
  Object.entries(stafferPets.PALETTES) as [
    StafferPetPaletteId,
    StafferPetPalette,
  ][]
).sort(([, left], [, right]) => left.rarityRank - right.rarityRank);

// Lucky Roll outcomes — chances must sum to 100. The one-time roll unlocks
// exactly one of these colorways for the account.
const ROLL_PALETTE_CHANCES = [
  { id: "AURORA", chancePct: 45 },
  { id: "ROSE", chancePct: 45 },
  { id: "NEBULA", chancePct: 7 },
  { id: "GOLD", chancePct: 3 },
] as const satisfies { id: StafferPetPaletteId; chancePct: number }[];

const rollForPaletteId = (
  chances: readonly { id: StafferPetPaletteId; chancePct: number }[],
): StafferPetPaletteId => {
  const totalPct = chances.reduce((sum, chance) => sum + chance.chancePct, 0);
  const roll = Math.random() * totalPct;
  let cumulative = 0;
  for (const entry of chances) {
    cumulative += entry.chancePct;
    if (roll < cumulative) return entry.id;
  }
  return chances[chances.length - 1].id;
};
const SPECIES_ICON_BY_ID = {
  FOX: PawPrintIcon,
  DRAGON: FlameIcon,
  CAT: CatIcon,
  BLUE_JAY: BirdIcon,
  CARDINAL: FeatherIcon,
  COCKATIEL: EggIcon,
  KINGFISHER: FishIcon,
  OWL: MoonIcon,
  CROW: EyeIcon,
  WREN: BabyIcon,
  BALD_EAGLE: FlagIcon,
  DUNE_WORM: WormIcon,
  BAT: MoonIcon,
  PHOENIX: FlameIcon,
  DIREWOLF: PawPrintIcon,
  PETER: BirdIcon,
} as const satisfies Record<StafferPetSpecies, typeof BirdIcon>;

export const StafferPetAdoptionWizard = ({
  open,
  onBack,
  onComplete,
  onOpenChange,
}: {
  open: boolean;
  onBack: () => void;
  onComplete: (paletteId: StafferPetPaletteId) => void;
  onOpenChange: (open: boolean) => void;
}) => {
  const {
    currentUser,
    pet: currentUserPet,
    pets,
    unlockedColorIds: savedUnlockedColorIds,
  } = useStafferPet();
  // The one-time roll lives on the pet record. `hasRolled` gates whether the
  // account can still roll; `unlocked.colorIds` lists every roll colorway they
  // can select. A roll this session is layered on locally.
  /* Database CRUD Operations */
  // Update
  const { mutate: upsertPet, isPending: isSaving } = useUpsertPet();

  /* React Hooks */
  const [species, setSpecies] = useState<StafferPetSpecies>(
    currentUserPet?.equipped.petId ?? "FOX",
  );
  const [paletteId, setPaletteId] = useState<StafferPetPaletteId>(
    currentUserPet?.equipped.colorId ?? "EMBER",
  );
  const [topperId, setTopperId] = useState<string | null>(
    currentUserPet?.equipped.topperId ?? null,
  );
  const [topperOffsets, setTopperOffsets] = useState<
    Record<string, StafferPetTopperOffset>
  >(currentUserPet?.topperOffsets ?? {});
  const [hasTopperOffsetChanges, setHasTopperOffsetChanges] = useState(false);
  const [accessoryId, setAccessoryId] = useState<string | null>(
    currentUserPet?.equipped.accessoryId ?? null,
  );
  const [accessoryId2, setAccessoryId2] = useState<string | null>(
    currentUserPet?.equipped.accessoryId2 ?? null,
  );
  const [selectedAccessorySlot, setSelectedAccessorySlot] = useState<1 | 2>(1);
  const [accessoryAdjustments, setAccessoryAdjustments] = useState<
    Record<string, StafferPetTopperOffset>
  >(currentUserPet?.accessoryAdjustments ?? {});
  const [hasAccessoryAdjustmentChanges, setHasAccessoryAdjustmentChanges] =
    useState(false);
  const [rollCount, setRollCount] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const [rollResult, setRollResult] = useState<StafferPetPaletteId | null>(
    null,
  );
  // Seed the form from the saved pet exactly once per open, after the users
  // query resolves — re-seeding on every currentUserPet change would clobber
  // in-session edits (e.g. the roll winner) when a save round-trips back.
  const seededRef = useRef(false);

  const hasRolledOnAccount =
    (currentUserPet?.hasRolled ?? false) || rollResult !== null;
  // A roll this session unlocks its colorway once the spin settles, before the
  // save round-trips. Held back while rolling so the winner stays locked (dim +
  // lock badge) until the reveal — otherwise you'd see the result mid-spin.
  const unlockedColorIds = useMemo<StafferPetPaletteId[]>(() => {
    const owned = savedUnlockedColorIds;
    // While rolling, strip the winner even if its persist already round-tripped
    // into the cache — keeps it locked (dim + lock badge) until the reveal.
    if (isRolling && rollResult !== null) {
      return owned.filter((id) => id !== rollResult);
    }
    if (rollResult !== null && !owned.includes(rollResult)) {
      return [...owned, rollResult];
    }
    return [...owned];
  }, [savedUnlockedColorIds, rollResult, isRolling]);
  const unlockedTopperOptions = useMemo(
    () =>
      Object.entries(stafferPets.TOPPERS).filter(([id]) =>
        currentUserPet?.unlocked.topperIds.includes(id),
      ),
    [currentUserPet],
  );
  const unlockedAccessoryOptions = useMemo(
    () =>
      Object.entries(stafferPets.ACCESSORIES).filter(([id]) =>
        currentUserPet?.unlocked.accessoryIds?.includes(id),
      ),
    [currentUserPet],
  );
  const availableSpecies = useMemo(
    () => stafferPets.getSelectableSpecies(currentUserPet),
    [currentUserPet],
  );
  const selectedTopperOffset = stafferPets.getTopperOffset(
    topperOffsets,
    species,
    topperId,
  );
  const selectedAccessoryAdjustment = stafferPets.getAccessoryAdjustment(
    accessoryAdjustments,
    species,
    selectedAccessorySlot === 1 ? accessoryId : accessoryId2,
  );
  const firstAccessoryAdjustment = stafferPets.getAccessoryAdjustment(
    accessoryAdjustments,
    species,
    accessoryId,
  );
  const secondAccessoryAdjustment = stafferPets.getAccessoryAdjustment(
    accessoryAdjustments,
    species,
    accessoryId2,
  );
  const hasUnsavedChanges = useMemo(() => {
    const savedSpecies = currentUserPet?.equipped.petId ?? "FOX";
    const savedColor = currentUserPet?.equipped.colorId ?? "EMBER";
    const savedTopper = currentUserPet?.equipped.topperId ?? null;
    const savedAccessory = currentUserPet?.equipped.accessoryId ?? null;
    const savedAccessory2 = currentUserPet?.equipped.accessoryId2 ?? null;

    return (
      rollResult !== null ||
      species !== savedSpecies ||
      paletteId !== savedColor ||
      topperId !== savedTopper ||
      accessoryId !== savedAccessory ||
      accessoryId2 !== savedAccessory2 ||
      hasTopperOffsetChanges ||
      hasAccessoryAdjustmentChanges
    );
  }, [
    currentUserPet,
    accessoryId,
    accessoryId2,
    hasAccessoryAdjustmentChanges,
    hasTopperOffsetChanges,
    paletteId,
    rollResult,
    species,
    topperId,
  ]);

  const buildPetPayload = useCallback(
    (overrides: Partial<Pet>): Pet => {
      const base: Pet = currentUserPet ?? {
        userId: currentUser?.id ?? -1,
        equipped: {
          petId: species,
          colorId: "EMBER",
          topperId: null,
          accessoryId: null,
          accessoryId2: null,
        },
        unlocked: {
          petIds: [],
          colorIds: [],
          topperIds: [],
          accessoryIds: [],
        },
        topperOffsets: {},
        accessoryAdjustments: {},
        hasRolled: false,
        shouldHide: false,
        coinBalance: 0,
        totalCoinsEarned: 0,
        incentiveTokensEarned: 0,
        incentiveTokensRedeemed: 0,
        incentiveTokensAvailable: 0,
      };
      return { ...base, ...overrides };
    },
    [currentUser, currentUserPet, species],
  );

  // The saved inventory plus any colorways won locally this session but not
  // yet round-tripped into the cache — so a save never clobbers a fresh roll.
  const buildUnlocked = (
    extraColorIds: StafferPetPaletteId[],
  ): Pet["unlocked"] => ({
    petIds: currentUserPet?.unlocked.petIds ?? [],
    colorIds: [
      ...new Set([
        ...(currentUserPet?.unlocked.colorIds ?? []),
        ...extraColorIds,
      ]),
    ],
    topperIds: currentUserPet?.unlocked.topperIds ?? [],
    accessoryIds: currentUserPet?.unlocked.accessoryIds ?? [],
  });

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    // Wait for the pets query before seeding so the saved pet preloads.
    if (seededRef.current || pets === undefined) return;

    setSpecies(currentUserPet?.equipped.petId ?? "FOX");
    setPaletteId(currentUserPet?.equipped.colorId ?? "EMBER");
    setTopperId(currentUserPet?.equipped.topperId ?? null);
    setTopperOffsets(currentUserPet?.topperOffsets ?? {});
    setHasTopperOffsetChanges(false);
    setAccessoryId(currentUserPet?.equipped.accessoryId ?? null);
    setAccessoryId2(currentUserPet?.equipped.accessoryId2 ?? null);
    setSelectedAccessorySlot(1);
    setAccessoryAdjustments(currentUserPet?.accessoryAdjustments ?? {});
    setHasAccessoryAdjustmentChanges(false);
    setRollCount(0);
    setRollResult(null);
    seededRef.current = true;
  }, [open, pets, currentUserPet]);

  // Visual spin only. The winner is chosen (and persisted) in handleLuckyRoll,
  // so this just cycles the roll colorways and settles on it — no state writes
  // that could re-trigger a save.
  useEffect(() => {
    if (rollCount === 0 || rollResult === null) return;

    const rollPaletteIds = ROLL_PALETTE_CHANCES.map((chance) => chance.id);
    let paletteIndex = 0;
    setIsRolling(true);

    const colorRollInterval = window.setInterval(() => {
      setPaletteId(rollPaletteIds[paletteIndex % rollPaletteIds.length]);
      paletteIndex += 1;
    }, COLOR_ROLL_INTERVAL_MS);

    const colorRollTimeout = window.setTimeout(() => {
      window.clearInterval(colorRollInterval);
      setPaletteId(rollResult);
      setIsRolling(false);
    }, COLOR_ROLL_DURATION_MS);

    return () => {
      window.clearInterval(colorRollInterval);
      window.clearTimeout(colorRollTimeout);
    };
  }, [rollCount, rollResult]);

  /* Helpers */
  const renderLuckyRollLabel = () => {
    if (isRolling && rollCount > 0) return "ROLLING…";
    if (hasRolledOnAccount) return "ROLL USED";
    return "LUCKY ROLL · 1×";
  };

  const renderPaletteOption = (id: StafferPetPaletteId) => {
    if (id === "NONE") return null;

    if (stafferPets.isBasePaletteId(id)) {
      return (
        <PaletteOption
          key={id}
          isSelected={id === paletteId}
          paletteId={id}
          onSelect={handlePaletteSelect}
        />
      );
    }

    // Roll colorways pitch the dice until the account's one roll is used;
    // after that (and for shop-only colors) the Pet Shop is the unlock path.
    const rollOption = hasRolledOnAccount
      ? undefined
      : ROLL_PALETTE_CHANCES.find((option) => option.id === id);
    let unlockMethod: "EXCLUSIVE" | "ROLL" | "SHOP" = "SHOP";
    if (
      id === "PHX_RED" ||
      id === "CENTRE_COURT" ||
      id === "CHAMPIONSHIP_WHITE" ||
      id === "CORBIN"
    )
      unlockMethod = "EXCLUSIVE";
    else if (rollOption) unlockMethod = "ROLL";
    // Reward-only colorways stay hidden until the account owns them.
    if (unlockMethod === "EXCLUSIVE" && !unlockedColorIds.includes(id))
      return null;
    return (
      <RollPaletteButton
        key={id}
        paletteId={id}
        chancePct={rollOption?.chancePct}
        isUnlocked={unlockedColorIds.includes(id)}
        isSelected={id === paletteId}
        isSpotlit={isRolling && id === paletteId}
        onSelect={handlePaletteSelect}
        unlockMethod={unlockMethod}
      />
    );
  };

  /* Handlers */
  const handlePaletteSelect = (nextPaletteId: StafferPetPaletteId) => {
    setPaletteId(nextPaletteId);
  };

  const handleTopperOffsetChange = (offset: StafferPetTopperOffset) => {
    if (topperId === null) return;

    const key = stafferPets.getTopperOffsetKey(species, topperId);
    setTopperOffsets((current) => ({ ...current, [key]: offset }));
    setHasTopperOffsetChanges(true);
  };

  const handleTopperOffsetReset = () => {
    if (topperId === null) return;

    const key = stafferPets.getTopperOffsetKey(species, topperId);
    if (!(key in topperOffsets)) return;

    setTopperOffsets((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([offsetKey]) => offsetKey !== key),
      ),
    );
    setHasTopperOffsetChanges(true);
  };

  const handleTopperScaleChange = (amount: number) => {
    if (topperId === null) return;

    handleTopperOffsetChange(
      stafferPets.clampTopperOffset(
        {
          ...selectedTopperOffset,
          scale: (selectedTopperOffset.scale || 1) + amount,
        },
        topperId,
      ),
    );
  };

  const handleAccessoryAdjustmentChange = (
    slot: 1 | 2,
    adjustment: StafferPetTopperOffset,
  ) => {
    const activeAccessoryId = slot === 1 ? accessoryId : accessoryId2;
    if (activeAccessoryId === null) return;

    const key = stafferPets.getAccessoryAdjustmentKey(
      species,
      activeAccessoryId,
    );
    setAccessoryAdjustments((current) => ({
      ...current,
      [key]: stafferPets.clampAccessoryAdjustment(
        adjustment,
        activeAccessoryId,
      ),
    }));
    setHasAccessoryAdjustmentChanges(true);
  };

  const handleAccessoryScaleChange = (amount: number) => {
    if ((selectedAccessorySlot === 1 ? accessoryId : accessoryId2) === null)
      return;

    handleAccessoryAdjustmentChange(
      selectedAccessorySlot,
      stafferPets.clampAccessoryAdjustment(
        {
          ...selectedAccessoryAdjustment,
          scale: (selectedAccessoryAdjustment.scale || 1) + amount,
        },
        selectedAccessorySlot === 1 ? accessoryId : accessoryId2,
      ),
    );
  };

  const handleAccessoryAdjustmentReset = () => {
    const activeAccessoryId =
      selectedAccessorySlot === 1 ? accessoryId : accessoryId2;
    if (activeAccessoryId === null) return;

    const key = stafferPets.getAccessoryAdjustmentKey(
      species,
      activeAccessoryId,
    );
    if (!(key in accessoryAdjustments)) return;

    setAccessoryAdjustments((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([adjustmentKey]) => adjustmentKey !== key,
        ),
      ),
    );
    setHasAccessoryAdjustmentChanges(true);
  };

  const handleAccessoryUnequip = (slot: 1 | 2) => {
    if (slot === 1) setAccessoryId(null);
    else setAccessoryId2(null);
  };

  const handleLuckyRoll = () => {
    if (hasRolledOnAccount || isRolling || currentUser === null) {
      return;
    }

    // Only the colorways still unlockable (roll colors not already owned).
    const rollableChances = ROLL_PALETTE_CHANCES.filter(
      (chance) => !unlockedColorIds.includes(chance.id),
    );
    if (rollableChances.length === 0) return;

    const winner = rollForPaletteId(rollableChances);
    // Flip isRolling synchronously so the winner never flashes unlocked before
    // the spin effect (which also sets it) kicks in on the next tick.
    setIsRolling(true);
    setRollResult(winner);
    setRollCount((count) => count + 1);

    // Persist once: flip hasRolled and add the won colorway to the inventory.
    upsertPet({
      userId: currentUser.id,
      pet: buildPetPayload({
        hasRolled: true,
        unlocked: buildUnlocked([winner]),
      }),
    });
  };

  const handleSave = () => {
    if (currentUser === null || isRolling) return;
    upsertPet(
      {
        userId: currentUser.id,
        // Carry the roll state explicitly so a save never clobbers it, even if
        // the roll's own save hasn't round-tripped into currentUserPet yet.
        pet: buildPetPayload({
          equipped: {
            petId: species,
            colorId: paletteId,
            topperId,
            accessoryId,
            accessoryId2,
          },
          unlocked: buildUnlocked(rollResult === null ? [] : [rollResult]),
          topperOffsets,
          accessoryAdjustments,
          hasRolled: hasRolledOnAccount,
        }),
      },
      {
        onSuccess: () => {
          onComplete(paletteId);
          onOpenChange(false);
        },
      },
    );
  };

  /* Render */
  const renderSpeciesStep = () => {
    return (
      <Column className="items-center gap-7 py-3">
        <Row className="flex-wrap justify-center gap-2">
          {availableSpecies.map((option) => (
            <SpeciesOption
              key={option.id}
              isSelected={option.id === species}
              label={option.label}
              species={option.id}
              onSelect={setSpecies}
            />
          ))}
        </Row>
        <Column className="items-center gap-4">
          <Column className="h-60 w-60 items-center justify-center border-4 border-slate-900 bg-white p-3 shadow-[7px_7px_0_#172033]">
            <StafferPetSprite
              species={species}
              paletteId={paletteId}
              sizePx={PET_PREVIEW_SIZE_PX}
              topperId={topperId}
              topperOffset={selectedTopperOffset}
              onTopperOffsetChange={handleTopperOffsetChange}
              accessoryId={accessoryId}
              accessoryAdjustment={firstAccessoryAdjustment}
              onAccessoryAdjustmentChange={(adjustment) =>
                handleAccessoryAdjustmentChange(1, adjustment)
              }
              accessoryId2={accessoryId2}
              accessoryAdjustment2={secondAccessoryAdjustment}
              onAccessoryAdjustmentChange2={(adjustment) =>
                handleAccessoryAdjustmentChange(2, adjustment)
              }
              onAccessoryDragStart={setSelectedAccessorySlot}
            />
          </Column>
          <Text className="font-mono text-lg font-black tracking-[0.28em] text-slate-900">
            {stafferPets.PALETTES[paletteId].label.toUpperCase()}{" "}
            {stafferPets.getSpeciesLabel(species).toUpperCase()}
          </Text>
          {topperId !== null ? (
            <Row className="items-center gap-3">
              <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-violet-700">
                Drag topper to reposition
              </Text>
              <Row className="items-center gap-1">
                <Text className="mr-1 font-mono text-[8px] font-black uppercase tracking-[0.1em] text-slate-500">
                  Size
                </Text>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    handleTopperScaleChange(-0.1);
                  }}
                  className="h-7 w-7 rounded-none border-2 border-slate-900 p-0"
                >
                  <MinusIcon className="h-3 w-3" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    handleTopperScaleChange(0.1);
                  }}
                  className="h-7 w-7 rounded-none border-2 border-slate-900 p-0"
                >
                  <PlusIcon className="h-3 w-3" />
                </Button>
              </Row>
              <Button
                type="button"
                variant="ghost"
                onClick={handleTopperOffsetReset}
                className="h-7 rounded-none px-2 font-mono text-[8px] font-black uppercase tracking-[0.1em] text-slate-600 hover:bg-orange-100"
              >
                <RotateCcwIcon className="mr-1 h-3 w-3" />
                Reset
              </Button>
            </Row>
          ) : null}
          {(selectedAccessorySlot === 1 ? accessoryId : accessoryId2) !==
          null ? (
            <Row className="items-center gap-3">
              <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-violet-700">
                Drag either accessory to reposition
              </Text>
              <Row className="items-center gap-1">
                <Text className="mr-1 font-mono text-[8px] font-black uppercase tracking-[0.1em] text-slate-500">
                  Size
                </Text>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    handleAccessoryScaleChange(-0.1);
                  }}
                  className="h-7 w-7 rounded-none border-2 border-slate-900 p-0"
                >
                  <MinusIcon className="h-3 w-3" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    handleAccessoryScaleChange(0.1);
                  }}
                  className="h-7 w-7 rounded-none border-2 border-slate-900 p-0"
                >
                  <PlusIcon className="h-3 w-3" />
                </Button>
              </Row>
              <Button
                type="button"
                variant="ghost"
                onClick={handleAccessoryAdjustmentReset}
                className="h-7 rounded-none px-2 font-mono text-[8px] font-black uppercase tracking-[0.1em] text-slate-600 hover:bg-orange-100"
              >
                <RotateCcwIcon className="mr-1 h-3 w-3" />
                Reset
              </Button>
            </Row>
          ) : null}
        </Column>
        <Row className="w-full items-center gap-3 px-4">
          <Text className="w-12 shrink-0 font-mono text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
            Color
          </Text>
          <Row className="flex-wrap gap-3">
            {paletteOptions.map(([id]) => renderPaletteOption(id))}
          </Row>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outlineDark"
                aria-disabled={isRolling || hasRolledOnAccount}
                onClick={handleLuckyRoll}
                className={cn(
                  "h-12 w-12 shrink-0 rounded-[16777200px] border-2 border-[#1B2440] bg-[#E8823C] p-0 text-white shadow-[3px_3px_0_#1B2440] transition-transform duration-200 ease-in hover:scale-110 hover:bg-[#E8823C] hover:text-white",
                  (isRolling || hasRolledOnAccount) &&
                    "cursor-default opacity-60 hover:scale-100",
                )}
              >
                <DicesIcon className="h-6 w-6" />
                <Text className="sr-only">{renderLuckyRollLabel()}</Text>
              </Button>
            </TooltipTrigger>
            <TooltipContent
              sideOffset={8}
              side="top"
              className="font-semiBold rounded-none border-slate-500 bg-transparent px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-700 shadow-none"
            >
              {renderLuckyRollLabel()}
            </TooltipContent>
          </Tooltip>
        </Row>
        {unlockedTopperOptions.length > 0 ? (
          <Row className="w-full items-center gap-3 px-4">
            <Text className="w-12 shrink-0 font-mono text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
              Topper
            </Text>
            <TopperOption
              isSelected={topperId === null}
              topperId={null}
              onSelect={setTopperId}
            />
            {unlockedTopperOptions.map(([id]) => (
              <TopperOption
                key={id}
                isSelected={topperId === id}
                topperId={id}
                onSelect={setTopperId}
              />
            ))}
          </Row>
        ) : null}
        {unlockedAccessoryOptions.length > 0 ? (
          <Column className="w-full gap-2 px-4">
            <StafferPetAccessorySlots
              accessoryId={accessoryId}
              accessoryId2={accessoryId2}
              selectedSlot={selectedAccessorySlot}
              onSelect={setSelectedAccessorySlot}
              onUnequip={handleAccessoryUnequip}
            />
            <Row className="items-center gap-3">
              <Text className="w-12 shrink-0 font-mono text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
                Gear
              </Text>
              {unlockedAccessoryOptions.map(([id]) => (
                <AccessoryOption
                  key={id}
                  accessoryId={id}
                  isSelected={
                    (selectedAccessorySlot === 1
                      ? accessoryId
                      : accessoryId2) === id
                  }
                  onSelect={(selectedId) => {
                    if (selectedAccessorySlot === 1) {
                      setAccessoryId(selectedId);
                      if (selectedId === accessoryId2) setAccessoryId2(null);
                    } else {
                      setAccessoryId2(selectedId);
                      if (selectedId === accessoryId) setAccessoryId(null);
                    }
                  }}
                />
              ))}
            </Row>
          </Column>
        ) : null}
      </Column>
    );
  };

  const render = () => {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideCloseButton
          className="staffer-pet-pixel-modal h-auto w-[min(92vw,780px)] max-w-none gap-4 rounded-none border-4 border-slate-900 bg-[#fffaf0] px-6 pb-10 pt-4 sm:rounded-none"
        >
          <TooltipProvider delayDuration={150}>
            <DialogHeader className="border-b-4 border-slate-900 pb-3 text-left">
              <Row className="w-full items-center justify-between gap-4">
                <Column className="gap-0">
                  <DialogTitle className="font-mono text-xl font-black uppercase tracking-[0.1em] text-slate-900">
                    Staffer Pet
                  </DialogTitle>
                  <DialogDescription className="font-mono text-xs uppercase tracking-[0.18em] text-orange-600">
                    Playground v0.1
                  </DialogDescription>
                </Column>
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 p-0 text-slate-900 hover:bg-orange-100"
                  >
                    <XIcon />
                    <Text className="sr-only">Close</Text>
                  </Button>
                </DialogClose>
              </Row>
            </DialogHeader>
            {renderSpeciesStep()}
            <Row className="items-center gap-3 border-t-4 border-slate-900 pt-4">
              <Button
                type="button"
                variant="outlineDark"
                onClick={onBack}
                className="h-12 w-[18%] min-w-28 border-2 border-slate-900 bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.1em] text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Yard
              </Button>
              <Button
                type="button"
                variant="outlineDark"
                disabled={isRolling || isSaving}
                onClick={handleSave}
                className={cn(
                  "h-12 flex-1 border-2 border-slate-900 bg-slate-900 px-8 py-2 font-mono text-sm font-bold uppercase tracking-[0.12em] text-orange-50 shadow-[3px_3px_0_#f97316] transition-shadow hover:bg-slate-800 disabled:opacity-60",
                  hasUnsavedChanges &&
                    !isRolling &&
                    !isSaving &&
                    "staffer-cta-blink",
                )}
              >
                {isSaving ? "SAVING…" : "SAVE PET"}
              </Button>
            </Row>
          </TooltipProvider>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};

const SpeciesOption = ({
  isSelected,
  label,
  species,
  onSelect,
}: {
  isSelected: boolean;
  label: string;
  species: StafferPetSpecies;
  onSelect: (species: StafferPetSpecies) => void;
}) => {
  const SpeciesIcon = SPECIES_ICON_BY_ID[species];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outlineDark"
          onClick={() => {
            onSelect(species);
          }}
          className={cn(
            "h-12 w-12 rounded-none p-1",
            isSelected
              ? "border-2 border-slate-900 bg-orange-400 text-white shadow-[3px_3px_0_#172033] hover:bg-orange-500 hover:text-white"
              : "border-2 bg-white text-[#1B2440] shadow-none hover:bg-orange-100",
          )}
        >
          <SpeciesIcon className="h-6 w-6" />
          <Text className="sr-only">{label}</Text>
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        className="font-semiBold border-0 bg-transparent px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-700 shadow-none"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

const PaletteOption = ({
  isSelected,
  paletteId,
  onSelect,
}: {
  isSelected: boolean;
  paletteId: StafferPetPaletteId;
  onSelect: (paletteId: StafferPetPaletteId) => void;
}) => {
  const palette = stafferPets.PALETTES[paletteId];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outlineDark"
          onClick={() => {
            onSelect(paletteId);
          }}
          className={cn(
            "h-9 w-9 rounded-none border-2 p-0",
            isSelected
              ? "border-2 border-slate-900 shadow-[3px_3px_0_#172033]"
              : "border-transparent hover:border-slate-900",
          )}
          style={{
            background: stafferPets.getPaletteSwatchBackground(paletteId),
          }}
        >
          <Text className="sr-only">{palette.label}</Text>
        </Button>
      </TooltipTrigger>
      <TooltipContent
        sideOffset={8}
        side="top"
        className="font-semiBold rounded-none border-slate-500 bg-transparent px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-700 shadow-none"
      >
        {palette.label}
      </TooltipContent>
    </Tooltip>
  );
};

const TopperOption = ({
  isSelected,
  topperId,
  onSelect,
}: {
  isSelected: boolean;
  topperId: string | null;
  onSelect: (topperId: string | null) => void;
}) => {
  const topper = stafferPets.getTopper(topperId);

  /* Render */
  const renderContent = () => {
    if (topper === null) return <XIcon className="h-5 w-5" />;
    return (
      <img
        alt=""
        src={topper.src}
        className="h-full w-full object-contain"
        style={{ imageRendering: "pixelated" }}
      />
    );
  };

  const render = () => {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outlineDark"
            onClick={() => {
              onSelect(topperId);
            }}
            className={cn(
              "h-11 w-11 rounded-none border-2 bg-white p-1 text-slate-500",
              isSelected
                ? "border-slate-900 bg-orange-100 shadow-[3px_3px_0_#172033]"
                : "border-transparent hover:border-slate-900 hover:bg-orange-50",
            )}
          >
            {renderContent()}
            <Text className="sr-only">{topper?.label ?? "No topper"}</Text>
          </Button>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={8}
          side="bottom"
          className="font-semiBold rounded-none border-slate-500 bg-transparent px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-700 shadow-none"
        >
          {topper?.label || "No topper"}
        </TooltipContent>
      </Tooltip>
    );
  };

  return render();
};

const AccessoryOption = ({
  accessoryId,
  isSelected,
  onSelect,
}: {
  accessoryId: string | null;
  isSelected: boolean;
  onSelect: (accessoryId: string | null) => void;
}) => {
  const accessory = stafferPets.getAccessory(accessoryId);

  /* Render */
  const renderContent = () => {
    if (accessory === null) return <XIcon className="h-5 w-5" />;
    return (
      <img
        alt=""
        src={accessory.src}
        className="h-full w-full object-contain"
        style={{ imageRendering: "pixelated" }}
      />
    );
  };

  const render = () => {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outlineDark"
            onClick={() => {
              onSelect(accessoryId);
            }}
            className={cn(
              "h-11 w-11 rounded-none border-2 bg-white p-1 text-slate-500",
              isSelected
                ? "border-slate-900 bg-orange-100 shadow-[3px_3px_0_#172033]"
                : "border-transparent hover:border-slate-900 hover:bg-orange-50",
            )}
          >
            {renderContent()}
            <Text className="sr-only">
              {accessory?.label ?? "No accessory"}
            </Text>
          </Button>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={8}
          side="bottom"
          className="font-semiBold rounded-none border-slate-500 bg-transparent px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-700 shadow-none"
        >
          {accessory?.label || "No accessory"}
        </TooltipContent>
      </Tooltip>
    );
  };

  return render();
};

const RollPaletteButton = ({
  paletteId,
  chancePct,
  isUnlocked,
  isSelected,
  isSpotlit,
  onSelect,
  unlockMethod,
}: {
  paletteId: StafferPetPaletteId;
  chancePct?: number;
  isUnlocked: boolean;
  isSelected: boolean;
  isSpotlit: boolean;
  onSelect: (paletteId: StafferPetPaletteId) => void;
  unlockMethod: "EXCLUSIVE" | "ROLL" | "SHOP";
}) => {
  const palette = stafferPets.PALETTES[paletteId];
  const backgroundColor =
    isUnlocked || isSpotlit ? palette.body : `${palette.body}80`;

  const renderBadgeIcon = () => {
    if (unlockMethod === "EXCLUSIVE") {
      return <SparklesIcon className="h-3 w-3" />;
    }
    if (unlockMethod === "SHOP") {
      return <ShoppingBagIcon className="h-3 w-3" />;
    }
    return <DicesIcon className="h-3 w-3" />;
  };

  const renderTooltipLabel = () => {
    if (isUnlocked) return palette.label;
    if (unlockMethod === "EXCLUSIVE") {
      return `${palette.label} · EXCLUSIVE REWARD`;
    }
    if (unlockMethod === "SHOP") return `${palette.label} · PET SHOP`;
    return `🎲 ${palette.label} · ${String(chancePct ?? 0)}% CHANCE`;
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Column className="h-9 w-9">
          <Button
            type="button"
            disabled={!isUnlocked}
            variant="outlineDark"
            onClick={() => {
              onSelect(paletteId);
            }}
            className={cn(
              "relative h-full w-full rounded-none border-2 p-0 disabled:opacity-100",
              !isUnlocked && "border-dashed border-slate-900",
              isUnlocked &&
                !isSelected &&
                "border-transparent hover:border-slate-900",
              isUnlocked &&
                isSelected &&
                "border-2 border-slate-900 shadow-[3px_3px_0_#172033]",
              isSpotlit &&
                "scale-105 border-slate-900 shadow-[3px_3px_0_#f97316]",
            )}
            style={{ backgroundColor }}
          >
            {!isUnlocked && (
              <Column className="absolute -bottom-1 -right-1 h-6 w-6 items-center justify-center rounded-[23967428px] border-[1.429px] border-[#E8823C] bg-[#FDFBF3] text-slate-900">
                {renderBadgeIcon()}
              </Column>
            )}
            <Text className="sr-only">{palette.label}</Text>
          </Button>
        </Column>
      </TooltipTrigger>
      <TooltipContent
        sideOffset={8}
        side="top"
        className="font-semiBold rounded-none border-slate-500 bg-transparent px-2 py-1 font-mono text-[8px] uppercase tracking-[0.12em] text-slate-700 shadow-none"
      >
        {renderTooltipLabel()}
      </TooltipContent>
    </Tooltip>
  );
};
